import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import {
  MessageSquare, Plus, Send, AlertCircle, CheckCircle, Clock, MoreHorizontal,
  Bell, Inbox, ArrowRight, Loader2, Tag
} from 'lucide-react';
import { toast } from 'sonner';

const categories = ['billing', 'technical', 'feature_request', 'bug_report', 'general'];
const priorities = ['low', 'medium', 'high', 'urgent'];
const statusColors = {
  open: 'bg-blue-100 text-blue-700', in_progress: 'bg-amber-100 text-amber-700',
  resolved: 'bg-emerald-100 text-emerald-700', closed: 'bg-slate-100 text-slate-600'
};
const priorityColors = {
  low: 'bg-slate-100 text-slate-600', medium: 'bg-blue-100 text-blue-700',
  high: 'bg-amber-100 text-amber-700', urgent: 'bg-red-100 text-red-700'
};

export default function Communications() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tickets');
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [ticketForm, setTicketForm] = useState({ category: 'general', subject: '', message: '', priority: 'medium' });

  const fetchData = useCallback(async () => {
    try {
      const [ticketsRes, statsRes, notifsRes] = await Promise.all([
        api.get('/tickets'),
        api.get('/tickets/stats'),
        api.get('/notifications').catch(() => ({ data: [] }))
      ]);
      setTickets(ticketsRes.data);
      setStats(statsRes.data);
      setNotifications(notifsRes.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!ticketForm.subject || !ticketForm.message) { toast.error('Subject and message required'); return; }
    try {
      await api.post('/tickets', ticketForm);
      toast.success('Ticket created successfully');
      setShowNewTicket(false);
      setTicketForm({ category: 'general', subject: '', message: '', priority: 'medium' });
      fetchData();
    } catch (err) { toast.error('Failed to create ticket'); }
  };

  const handleReply = async () => {
    if (!replyMessage.trim() || !selectedTicket) return;
    setSending(true);
    try {
      await api.post(`/tickets/${selectedTicket.id}/respond`, { message: replyMessage });
      toast.success('Reply sent');
      setReplyMessage('');
      const updated = await api.get('/tickets');
      setTickets(updated.data);
      const found = updated.data.find(t => t.id === selectedTicket.id);
      if (found) setSelectedTicket(found);
    } catch (err) { toast.error('Failed to send reply'); }
    finally { setSending(false); }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await api.put(`/tickets/${ticketId}/status`, { status: newStatus });
      toast.success(`Ticket ${newStatus}`);
      fetchData();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, status: newStatus });
      }
    } catch (err) { toast.error('Failed'); }
  };

  if (loading) return (
    <div className="space-y-6" data-testid="communications-loading">
      <Skeleton className="h-8 w-48" />
      <div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>
      <Skeleton className="h-64 rounded-xl" />
    </div>
  );

  const byStatus = stats?.by_status || {};
  const isSuperAdmin = user?.role === 'super_admin';

  return (
    <div className="space-y-6" data-testid="communications-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold">Tickets</h1>
          <p className="text-sm text-muted-foreground">{isSuperAdmin ? 'Manage all academy tickets & notifications' : 'Support tickets & notifications'}</p>
        </div>
        <Dialog open={showNewTicket} onOpenChange={setShowNewTicket}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="new-ticket-button"><Plus className="h-4 w-4 mr-1" /> New Ticket</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create Support Ticket</DialogTitle></DialogHeader>
            <form onSubmit={handleCreateTicket} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Category</Label>
                  <Select value={ticketForm.category} onValueChange={(v) => setTicketForm({...ticketForm, category: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {categories.map(c => <SelectItem key={c} value={c} className="capitalize">{c.replace('_', ' ')}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Priority</Label>
                  <Select value={ticketForm.priority} onValueChange={(v) => setTicketForm({...ticketForm, priority: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {priorities.map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Subject *</Label>
                <Input value={ticketForm.subject} onChange={(e) => setTicketForm({...ticketForm, subject: e.target.value})} placeholder="Brief description of your issue" data-testid="ticket-subject-input" required />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Message *</Label>
                <Textarea value={ticketForm.message} onChange={(e) => setTicketForm({...ticketForm, message: e.target.value})} placeholder="Describe your issue in detail..." rows={4} data-testid="ticket-message-input" required />
              </div>
              <Button type="submit" className="w-full" data-testid="ticket-submit-button">
                <Send className="h-4 w-4 mr-2" /> Submit Ticket
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Status KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="hover:shadow-md transition-shadow duration-150">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1"><span className="text-xs text-muted-foreground">Open</span><AlertCircle className="h-4 w-4 text-blue-500" /></div>
            <p className="text-2xl font-display font-semibold tabular-nums">{byStatus.open || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1"><span className="text-xs text-muted-foreground">In Progress</span><Clock className="h-4 w-4 text-amber-500" /></div>
            <p className="text-2xl font-display font-semibold tabular-nums">{byStatus.in_progress || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1"><span className="text-xs text-muted-foreground">Resolved</span><CheckCircle className="h-4 w-4 text-emerald-500" /></div>
            <p className="text-2xl font-display font-semibold tabular-nums">{byStatus.resolved || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1"><span className="text-xs text-muted-foreground">Total</span><Inbox className="h-4 w-4 text-muted-foreground" /></div>
            <p className="text-2xl font-display font-semibold tabular-nums">{stats?.total || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tickets List + Detail */}
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Tickets List */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Tickets</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-border max-h-[500px] overflow-y-auto">
              {tickets.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  <MessageSquare className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" />
                  No tickets yet
                </div>
              ) : tickets.map((ticket) => (
                <button
                  key={ticket.id}
                  onClick={() => setSelectedTicket(ticket)}
                  className={`w-full text-left p-3 hover:bg-accent/50 transition-colors duration-150 ${selectedTicket?.id === ticket.id ? 'bg-accent/50 border-l-2 border-primary' : ''}`}
                  data-testid={`ticket-item-${ticket.id}`}
                >
                  <div className="flex items-start justify-between mb-1">
                    <span className="text-xs font-mono text-muted-foreground">{ticket.custom_id}</span>
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${statusColors[ticket.status] || 'bg-slate-100'}`}>{ticket.status?.replace('_', ' ')}</span>
                  </div>
                  <p className="text-sm font-medium truncate">{ticket.subject}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${priorityColors[ticket.priority] || 'bg-slate-100'}`}>{ticket.priority}</span>
                    {isSuperAdmin && ticket.academy_name && <span className="text-[10px] text-muted-foreground truncate">{ticket.academy_name}</span>}
                    <span className="text-[10px] text-muted-foreground">{new Date(ticket.created_at).toLocaleDateString()}</span>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Ticket Detail */}
        <Card className="lg:col-span-2">
          {selectedTicket ? (
            <>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-muted-foreground">{selectedTicket.custom_id}</span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[selectedTicket.status]}`}>{selectedTicket.status?.replace('_', ' ')}</span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${priorityColors[selectedTicket.priority]}`}>{selectedTicket.priority}</span>
                    </div>
                    <CardTitle className="text-base">{selectedTicket.subject}</CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">
                      By {selectedTicket.created_by_name} {selectedTicket.academy_name ? `(${selectedTicket.academy_name})` : ''} on {new Date(selectedTicket.created_at).toLocaleString()}
                    </p>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleStatusChange(selectedTicket.id, 'in_progress')}><Clock className="h-4 w-4 mr-2" /> Mark In Progress</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleStatusChange(selectedTicket.id, 'resolved')}><CheckCircle className="h-4 w-4 mr-2" /> Mark Resolved</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleStatusChange(selectedTicket.id, 'closed')}>Close Ticket</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                {/* Original Message */}
                <div className="p-3 rounded-lg bg-muted/50 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline" className="text-xs capitalize">{selectedTicket.category?.replace('_', ' ')}</Badge>
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{selectedTicket.message}</p>
                </div>

                {/* Responses */}
                <div className="space-y-3 mb-4 max-h-[250px] overflow-y-auto">
                  {(selectedTicket.responses || []).map((resp) => (
                    <div key={resp.id} className={`p-3 rounded-lg text-sm ${resp.user_role === 'super_admin' ? 'bg-primary/5 border-l-2 border-primary' : 'bg-muted/30'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-xs">{resp.user_name}</span>
                        <Badge variant="outline" className="text-[10px]">{resp.user_role?.replace('_', ' ')}</Badge>
                        <span className="text-[10px] text-muted-foreground">{new Date(resp.created_at).toLocaleString()}</span>
                      </div>
                      <p className="whitespace-pre-wrap">{resp.message}</p>
                    </div>
                  ))}
                </div>

                {/* Reply Box */}
                {selectedTicket.status !== 'closed' && (
                  <div className="flex gap-2">
                    <Input value={replyMessage} onChange={(e) => setReplyMessage(e.target.value)} placeholder="Type your reply..." className="flex-1"
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleReply(); }}}
                      data-testid="ticket-reply-input" />
                    <Button onClick={handleReply} disabled={sending || !replyMessage.trim()} data-testid="ticket-reply-send">
                      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </Button>
                  </div>
                )}
              </CardContent>
            </>
          ) : (
            <CardContent className="flex flex-col items-center justify-center py-16">
              <MessageSquare className="h-12 w-12 text-muted-foreground/30 mb-4" />
              <h3 className="text-base font-semibold mb-1">Select a ticket</h3>
              <p className="text-sm text-muted-foreground">Click on a ticket to view details and respond</p>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
}
